import apiServices from "../apiServices";
import { validateOrThrowApiResponse } from "../response-validator";

type CompleteTheSentenceChoice = {
    word: string;
    word_sound?: string | null;
    answer: string | null;
    answer_sound?: string | null;
    answer_image?: string | null;
};

type CompleteTheSentenceWrongAnswerGroup = {
    wrong_answer: Array<{
        text?: string | null;
        sound?: string | null;
        image?: string | null;
    }>;
};

type CompleteTheSentenceItem = {
    id?: number | string;
    no: number;
    expected_time?: number;
    question_answer: Array<CompleteTheSentenceChoice | CompleteTheSentenceWrongAnswerGroup>;
    hint?: string | null;
    sound_question_answer?: string | null;
    image_question_answer?: string | null;
    sound_hint?: string | null;
    image_hint?: string | null;
};

type SkillPayload = {
    skill_listening_speaking?: boolean;
    skill_lang_nature?: boolean;
    skill_reading?: boolean;
    skill_writing?: boolean;
};

type AudioPayload = {
    audio_question?: boolean;
    audio_choice?: boolean;
    audio_assistant_voice?: boolean;
    audio_background?: boolean;
};

type CreateByGamePayload = SkillPayload & AudioPayload & {
    gameId: number | string;
    complete_the_sentences: CompleteTheSentenceItem[];
};

type CreatePayload = SkillPayload & AudioPayload & {
    complete_the_sentences: CompleteTheSentenceItem[];
};

type UpdateCompletePayload = AudioPayload & {
    id: number | string;
    no?: number;
    expected_time?: number;
    question_answer?: Array<CompleteTheSentenceChoice | CompleteTheSentenceWrongAnswerGroup>;
    hint?: string | null;
    sound_question_answer?: string | null;
    image_question_answer?: string | null;
    sound_hint?: string | null;
    image_hint?: string | null;
    audio_question?: boolean;
    audio_choice?: boolean;
    audio_assistant_voice?: boolean;
    audio_background?: boolean;
    skill_listening_speaking?: boolean;
    skill_lang_nature?: boolean;
    skill_reading?: boolean;
    skill_writing?: boolean;
};

type DeletePayload = {
    id: number | string;
};

const COMPLETE_THE_SENTENCE_MEDIA_FIELDS = [
    "sound_question",
    "image_question",
    "hint",
    "sound_hint",
    "image_hint",
] as const;

type CompleteTheSentenceMediaField = (typeof COMPLETE_THE_SENTENCE_MEDIA_FIELDS)[number];

type HardDeleteMediaFieldPayload = {
    id: number | string;
    field: CompleteTheSentenceMediaField;
};

const completeTheSentenceAPI = {

    createByGame(payload: CreateByGamePayload) {
        const requestBody: any = {
            complete_the_sentences: payload.complete_the_sentences,
        };

        if (payload.skill_listening_speaking !== undefined) requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        if (payload.skill_lang_nature !== undefined) requestBody.skill_lang_nature = payload.skill_lang_nature;
        if (payload.skill_reading !== undefined) requestBody.skill_reading = payload.skill_reading;
        if (payload.skill_writing !== undefined) requestBody.skill_writing = payload.skill_writing;
        if (payload.audio_question !== undefined) requestBody.audio_question = payload.audio_question;
        if (payload.audio_choice !== undefined) requestBody.audio_choice = payload.audio_choice;
        if (payload.audio_assistant_voice !== undefined) requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        if (payload.audio_background !== undefined) requestBody.audio_background = payload.audio_background;

        return apiServices
            .post(`/api/game/${payload.gameId}/complete-the-sentences`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error createByGame complete-the-sentences:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    create(payload: CreatePayload) {
        const requestBody: any = {
            complete_the_sentences: payload.complete_the_sentences,
        };

        if (payload.skill_listening_speaking !== undefined) requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        if (payload.skill_lang_nature !== undefined) requestBody.skill_lang_nature = payload.skill_lang_nature;
        if (payload.skill_reading !== undefined) requestBody.skill_reading = payload.skill_reading;
        if (payload.skill_writing !== undefined) requestBody.skill_writing = payload.skill_writing;
        if (payload.audio_question !== undefined) requestBody.audio_question = payload.audio_question;
        if (payload.audio_choice !== undefined) requestBody.audio_choice = payload.audio_choice;
        if (payload.audio_assistant_voice !== undefined) requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        if (payload.audio_background !== undefined) requestBody.audio_background = payload.audio_background;

        return apiServices
            .post(`/api/complete-the-sentences`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error create complete-the-sentences:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    update(payload: UpdateCompletePayload) {
        const requestBody: any = {};
        if (payload.no !== undefined) requestBody.no = payload.no;
        if (payload.expected_time !== undefined) requestBody.expected_time = payload.expected_time;
        if (payload.question_answer !== undefined) requestBody.question_answer = payload.question_answer;
        if (payload.hint !== undefined) requestBody.hint = payload.hint;
        if (payload.sound_question_answer !== undefined) requestBody.sound_question_answer = payload.sound_question_answer;
        if (payload.image_question_answer !== undefined) requestBody.image_question_answer = payload.image_question_answer;
        if (payload.sound_hint !== undefined) requestBody.sound_hint = payload.sound_hint;
        if (payload.image_hint !== undefined) requestBody.image_hint = payload.image_hint;
        if (payload.audio_question !== undefined) requestBody.audio_question = payload.audio_question;
        if (payload.audio_choice !== undefined) requestBody.audio_choice = payload.audio_choice;
        if (payload.audio_assistant_voice !== undefined) requestBody.audio_assistant_voice = payload.audio_assistant_voice;
        if (payload.audio_background !== undefined) requestBody.audio_background = payload.audio_background;
        if (payload.skill_listening_speaking !== undefined) requestBody.skill_listening_speaking = payload.skill_listening_speaking;
        if (payload.skill_lang_nature !== undefined) requestBody.skill_lang_nature = payload.skill_lang_nature;
        if (payload.skill_reading !== undefined) requestBody.skill_reading = payload.skill_reading;
        if (payload.skill_writing !== undefined) requestBody.skill_writing = payload.skill_writing;

        return apiServices
            .put(`/api/complete-the-sentences/${payload.id}`, requestBody, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error update complete-the-sentences:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDelete(payload: DeletePayload) {
        return apiServices
            .delete(`/api/complete-the-sentences/${payload.id}`, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error delete complete-the-sentences:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    softDelete(payload: DeletePayload) {
        return apiServices
            .patch(`/api/complete-the-sentences/${payload.id}/soft-delete`, {}, {
                headers: {
                    Accept: "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error soft-delete complete-the-sentences:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

    hardDeleteMediaFileAndHint(payload: HardDeleteMediaFieldPayload) {
        const allowed = new Set<string>(COMPLETE_THE_SENTENCE_MEDIA_FIELDS as readonly string[]);
        if (!allowed.has(String(payload.field))) {
            return Promise.resolve({
                status: "failed",
                errMessage: `invalid field: ${String(payload.field)}`,
                error: "invalid_field",
            });
        }

        return apiServices
            .patch(`/api/complete-the-sentences/${payload.id}/hard-delete-field`, { field: payload.field }, {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            })
            .then((res) => validateOrThrowApiResponse(res))
            .catch((err) => {
                console.log("Error hardDeleteMediaFileAndHint:", err);
                return {
                    status: "failed",
                    errMessage: err?.message || err,
                    error: err,
                };
            });
    },

}

export default completeTheSentenceAPI;